/**
 * Tentpole Calendar admin API client.
 *
 * Backend: backend/src/routes/tentpoleCalendarAdmin.ts, mounted at
 * /api/admin/tentpole-calendar. Reads require `sales.view`; mutations require
 * `tentpoleCalendar.manage`.
 */

import { apiRequest } from './api';
import {
  AdminEntryInput,
  AdminEntryPatch,
  CalendarEntry,
  CalendarEntryStatus,
  CalendarPageOption,
  EntryFilters,
  Tentpole,
  TentpoleInput,
} from '../components/Sales/TentpoleCalendar/types';

const BASE = '/api/admin/tentpole-calendar';

// --- Tentpoles ---------------------------------------------------------------

export async function listTentpoles(includeInactive = false): Promise<Tentpole[]> {
  const body = await apiRequest(`${BASE}/tentpoles${includeInactive ? '?includeInactive=1' : ''}`);
  return body.tentpoles ?? [];
}

export async function createTentpole(input: TentpoleInput): Promise<Tentpole> {
  const body = await apiRequest(`${BASE}/tentpoles`, { method: 'POST', body: JSON.stringify(input) });
  return body.tentpole;
}

export async function updateTentpole(slug: string, patch: Partial<TentpoleInput>): Promise<Tentpole> {
  const body = await apiRequest(`${BASE}/tentpoles/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return body.tentpole;
}

export async function deleteTentpole(slug: string): Promise<void> {
  await apiRequest(`${BASE}/tentpoles/${encodeURIComponent(slug)}`, { method: 'DELETE' });
}

// --- Entries -----------------------------------------------------------------

export async function listCalendarEntries(
  filters: EntryFilters = {},
): Promise<{ entries: CalendarEntry[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  const body = await apiRequest(`${BASE}/entries${qs ? `?${qs}` : ''}`);
  return { entries: body.entries ?? [], nextCursor: body.nextCursor ?? null };
}

export interface CalendarReviewQueuePage {
  entries: CalendarEntry[];
  nextCursor: string | null;
  /** Authoritative submitted count when supplied by the queue endpoint. */
  total?: number;
}

/** Authoritative badge/count; never derive this from a paginated list. */
export async function getCalendarPendingCount(): Promise<number> {
  const body = await apiRequest(`${BASE}/pending-count`);
  const raw = body.count ?? body.pendingCount ?? body.totalCount ?? 0;
  const count = Number(raw);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

/** Submitted entries ordered oldest-first for the review work queue. */
export async function listCalendarReviewQueue(
  opts: { limit?: number; cursor?: string } = {},
): Promise<CalendarReviewQueuePage> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  const qs = params.toString();
  const body = await apiRequest(`${BASE}/review-queue${qs ? `?${qs}` : ''}`);
  const totalRaw = body.total ?? body.count ?? body.pendingCount ?? body.totalCount;
  const total = totalRaw === undefined ? undefined : Number(totalRaw);
  const entries: CalendarEntry[] = [...(body.entries ?? [])].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  return {
    entries,
    nextCursor: body.nextCursor ?? null,
    ...(total !== undefined && Number.isFinite(total) && total >= 0 ? { total } : {}),
  };
}

export async function getCalendarEntry(id: string): Promise<{ entry: CalendarEntry; tentpole: Tentpole | null }> {
  const body = await apiRequest(`${BASE}/entries/${encodeURIComponent(id)}`);
  return { entry: body.entry, tentpole: body.tentpole ?? null };
}

/**
 * Idempotent on `entryId`: an identical retry answers 200 with the stored
 * plan, a changed retry 409s `already_saved` (body.entry is the stored plan),
 * and an id another owner holds 409s `id_conflict`.
 */
export async function createCalendarEntry(input: AdminEntryInput): Promise<CalendarEntry> {
  const body = await apiRequest(`${BASE}/entries`, { method: 'POST', body: JSON.stringify(input) });
  return body.entry;
}

/**
 * `expectedUpdatedAt` is the updatedAt the caller last saw; the server refuses
 * the edit with 409 `stale` when the plan has changed since.
 */
export async function updateCalendarEntry(
  id: string,
  patch: AdminEntryPatch,
  expectedUpdatedAt?: string,
): Promise<CalendarEntry> {
  const body = await apiRequest(`${BASE}/entries/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(expectedUpdatedAt ? { ...patch, expectedUpdatedAt } : patch),
  });
  return body.entry;
}

/** Permanently remove an entry from the Admin and Creator/Agency calendars. */
export async function deleteCalendarEntry(id: string): Promise<void> {
  await apiRequest(`${BASE}/entries/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** `expectedUpdatedAt` works as for updateCalendarEntry (409 `stale`). */
export async function changeCalendarEntryStatus(
  id: string,
  to: CalendarEntryStatus,
  note?: string,
  expectedUpdatedAt?: string,
): Promise<CalendarEntry> {
  const body = await apiRequest(`${BASE}/entries/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({
      to,
      ...(note ? { note } : {}),
      ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
    }),
  });
  return body.entry;
}

// --- Creator-page picker -------------------------------------------------------

export async function listCalendarPages(): Promise<CalendarPageOption[]> {
  const body = await apiRequest(`${BASE}/creator-pages`);
  return body.pages ?? [];
}
